import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Video, 
  Mic, 
  MicOff,
  StopCircle, 
  RotateCcw,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Info,
  AlertTriangle,
  MessageSquare,
  Shield,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo banner component for interview page
const SimulatedBanner = () => (
  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6">
    <div className="flex items-start space-x-3">
      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-amber-800">Simulated / Demo – Not Live AI Analysis</h4>
        <p className="text-sm text-amber-700 mt-1">
          This video interview feature provides simulated feedback for demonstration purposes only. 
          Your video is securely stored for review by our recruitment team. Full AI analysis capabilities 
          will be available in a future release.
        </p>
      </div>
    </div>
  </div>
);

const VideoInterview = () => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  
  // Questions
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  
  // Upload & Analysis state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [error, setError] = useState(null);
  
  // Refs
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
    return () => {
      stopCamera();
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/video-interview/questions`);
      const data = await response.json();
      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      // Fallback questions
      setQuestions([
        "Can you briefly describe your professional background and current role?",
        "Why are you interested in working in Saudi Arabia or the Middle East?",
        "How do you ensure professionalism and patient safety in your daily practice?",
        "Describe a challenging situation at work and how you handled it.",
        "How do you adapt when working under pressure or in a new environment?",
        "What values are most important to you as a healthcare professional?"
      ]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: micEnabled
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
      setError(null);
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera/microphone. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    
    chunksRef.current = [];
    
    const options = { mimeType: 'video/webm;codecs=vp9,opus' };
    try {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
    } catch (e) {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
    }
    
    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };
    
    mediaRecorderRef.current.start(1000);
    setIsRecording(true);
    setRecordingTime(0);
    
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setCurrentQuestionIndex(0);
    setAnalysisResults(null);
    setAnalysisStatus(null);
    setInterviewId(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const uploadAndAnalyze = async () => {
    if (!recordedBlob) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to submit your interview');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('video', recordedBlob, 'interview.webm');
      
      // Upload video
      const response = await fetch(`${API_URL}/api/video-interview/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const data = await response.json();
      setInterviewId(data.interview_id);
      setAnalysisStatus('analyzing');
      setUploadProgress(100);
      
      // Start polling for results
      pollForResults(data.interview_id, token);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload video');
      setIsUploading(false);
    }
  };

  const pollForResults = (id, token) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds max
    
    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      
      if (attempts >= maxAttempts) {
        clearInterval(pollIntervalRef.current);
        setAnalysisStatus('timeout');
        setError('Processing is taking longer than expected. Please check back later.');
        setIsUploading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/video-interview/results/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          setAnalysisResults(data);
          setAnalysisStatus('completed');
          setIsUploading(false);
        } else if (data.status === 'error') {
          clearInterval(pollIntervalRef.current);
          setAnalysisStatus('error');
          setError(data.message || 'Processing failed');
          setIsUploading(false);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 1000);
  };

  // Show SIMULATED analysis results
  if (analysisStatus === 'completed' && analysisResults) {
    const results = analysisResults.final_results || {};
    
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Simulation Disclaimer Banner */}
            <SimulatedBanner />
            
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Video Interview Submitted</h1>
              <p className="text-gray-600">Your video has been securely stored for review</p>
            </div>

            {/* Simulated Feedback Card */}
            <Card className="mb-6 border-2 border-[#42b1c2]/30 bg-[#42b1c2]/5">
              <CardHeader>
                <CardTitle className="flex items-center text-[#42b1c2]">
                  <Sparkles className="h-5 w-5 mr-2" />
                  Demo Feedback Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4 italic">
                  {results.disclaimer || "Simulated / Demo – Not Live AI Analysis. This feedback is for demonstration purposes only."}
                </p>
                
                {results.feedback && (
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center mb-2">
                        <MessageSquare className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="font-medium text-gray-700">Communication Clarity</span>
                      </div>
                      <p className="text-sm text-gray-600">{results.feedback.communication_clarity}</p>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center mb-2">
                        <Shield className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="font-medium text-gray-700">Confidence & Presence</span>
                      </div>
                      <p className="text-sm text-gray-600">{results.feedback.confidence_presence}</p>
                    </div>
                    
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center mb-2">
                        <CheckCircle2 className="h-4 w-4 text-teal-500 mr-2" />
                        <span className="font-medium text-gray-700">Professionalism</span>
                      </div>
                      <p className="text-sm text-gray-600">{results.feedback.professionalism}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recording Info */}
            {results.recording_info && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Recording Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{results.recording_info.questions_answered}</p>
                      <p className="text-xs text-gray-500">Questions Answered</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">✓</p>
                      <p className="text-xs text-gray-500">Recording Saved</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">✓</p>
                      <p className="text-xs text-gray-500">Ready for Review</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next Steps */}
            {results.next_steps && (
              <Card className="mb-6 border-emerald-200 bg-emerald-50/30">
                <CardHeader>
                  <CardTitle className="text-emerald-700 text-lg">What Happens Next</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {results.next_steps.map((step, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <ChevronRight className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700 text-sm">{step}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Important Notice */}
            <Card className="mb-6 border-gray-200 bg-gray-50">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <Info className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600 font-medium mb-1">Important Notice</p>
                    <p className="text-sm text-gray-500">
                      This demo feedback does not represent an actual AI assessment. No scoring, ranking, 
                      or hiring decisions are made based on this simulated output. Your video will be 
                      reviewed by our human recruitment team.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-center">
              <Button variant="outline" onClick={resetRecording} data-testid="record-new-interview-btn">
                <RotateCcw className="h-4 w-4 mr-2" />
                Record New Interview
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Video Interview</h1>
            <p className="text-gray-600">Record your responses to the interview questions below</p>
          </div>

          {/* Simulation Disclaimer Banner */}
          <SimulatedBanner />

          {/* Info Banner */}
          <Card className="mb-6 border border-[#42b1c2]/30 bg-[#42b1c2]/5">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-[#42b1c2] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">
                    Your video will be securely stored and reviewed by our recruitment team. 
                    These questions are designed for all healthcare professionals and do not contain 
                    specialty-specific clinical scenarios.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <p className="text-red-700">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Uploading/Analyzing State */}
          {(isUploading || analysisStatus === 'analyzing') && (
            <Card className="mb-6 border-2 border-[#42b1c2]/30">
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-[#42b1c2] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {isUploading && uploadProgress < 100 ? 'Uploading Video...' : 'Processing Your Submission...'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isUploading && uploadProgress < 100 
                    ? 'Please wait while your video is being uploaded'
                    : 'Generating demo feedback. This will only take a few seconds.'}
                </p>
                <Progress value={uploadProgress} className="w-full max-w-md mx-auto" />
                <p className="text-xs text-gray-500 mt-4">Simulated / Demo – Not Live AI Analysis</p>
              </CardContent>
            </Card>
          )}

          {/* Recording Interface */}
          {!isUploading && analysisStatus !== 'analyzing' && (
            <>
              {/* Questions Progress */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    4 General + 2 Situational Questions
                  </Badge>
                </div>
                <Progress value={(currentQuestionIndex / questions.length) * 100} className="h-2" />
              </div>

              {/* Current Question */}
              <Card className="mb-6 border-2 border-[#42b1c2]/30 bg-white">
                <CardContent className="p-6">
                  <p className="text-lg font-medium text-gray-900" data-testid="current-question">
                    {questions[currentQuestionIndex] || 'Loading question...'}
                  </p>
                  <div className="flex items-center space-x-2 mt-3">
                    <Badge variant="outline" className="text-xs">60-90 seconds recommended</Badge>
                    {currentQuestionIndex < 4 ? (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">General</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">Situational</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Video Preview */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      data-testid="video-preview"
                    />
                    
                    {!cameraReady && !recordedUrl && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gray-800">
                        <Video className="h-16 w-16 mb-4 text-gray-400" />
                        <Button onClick={startCamera} className="bg-[#42b1c2] hover:bg-[#3a9fb0]" data-testid="start-camera-btn">
                          <Video className="h-4 w-4 mr-2" />
                          Start Camera
                        </Button>
                      </div>
                    )}
                    
                    {recordedUrl && !isRecording && (
                      <video
                        src={recordedUrl}
                        controls
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="recorded-video"
                      />
                    )}
                    
                    {/* Recording indicator */}
                    {isRecording && (
                      <div className="absolute top-4 left-4 flex items-center space-x-2 bg-red-600 text-white px-3 py-1.5 rounded-full">
                        <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                        <span className="text-sm font-medium">{formatTime(recordingTime)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Controls */}
              <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                {cameraReady && !recordedUrl && (
                  <>
                    {!isRecording ? (
                      <Button 
                        onClick={startRecording}
                        size="lg"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="start-recording-btn"
                      >
                        <Video className="h-5 w-5 mr-2" />
                        Start Recording
                      </Button>
                    ) : (
                      <Button 
                        onClick={stopRecording}
                        size="lg"
                        variant="outline"
                        className="border-red-600 text-red-600 hover:bg-red-50"
                        data-testid="stop-recording-btn"
                      >
                        <StopCircle className="h-5 w-5 mr-2" />
                        Stop Recording
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={() => setMicEnabled(!micEnabled)}
                      className={!micEnabled ? 'text-red-600 border-red-600' : ''}
                      data-testid="mic-toggle-btn"
                    >
                      {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                    </Button>
                  </>
                )}
                
                {recordedUrl && (
                  <>
                    <Button variant="outline" onClick={resetRecording} data-testid="re-record-btn">
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Re-record
                    </Button>
                    
                    <Button 
                      onClick={uploadAndAnalyze}
                      size="lg"
                      className="bg-[#42b1c2] hover:bg-[#3a9fb0]"
                      data-testid="submit-interview-btn"
                    >
                      <Upload className="h-5 w-5 mr-2" />
                      Submit Interview
                    </Button>
                  </>
                )}
              </div>

              {/* Question Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestionIndex === 0 || isRecording}
                  data-testid="prev-question-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                  disabled={currentQuestionIndex === questions.length - 1 || isRecording}
                  data-testid="next-question-btn"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Tips */}
              <Card className="mt-8 border-gray-200 bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-lg">Recording Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="grid md:grid-cols-2 gap-3 text-sm text-gray-600">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Find a quiet, well-lit location</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Look directly at the camera when speaking</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Speak clearly at a moderate pace</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Dress professionally as you would for an interview</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Aim for 60-90 seconds per question</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Use specific examples from your experience</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Card className="mt-4 border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Simulated / Demo:</strong> Feedback shown after submission is for demonstration purposes only. 
                      No scoring, ranking, or hiring decisions are made based on this simulated output. 
                      Your video is securely stored and will be reviewed by our human recruitment team.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoInterview;
